/* eslint-disable react/require-default-props */
import React, { forwardRef, useRef, useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Icon from "../icon";

type DatePickerProps = {
  start_date: Date | null;
  end_date: Date | null;
  onDateChange: (dates: Date[]) => void;
  max_date?: Date | null;
  min_date?: Date | null;
};

type ButtonProps = React.HTMLProps<HTMLButtonElement>;

const DatePickerComponent = ({
  start_date,
  end_date,
  onDateChange,
  min_date = null,
  max_date,
}: DatePickerProps) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const datePickerRef = useRef<any>();
  const [open, setOpen] = useState(false);

  const getMonth = useMemo(() => {
    return (date: Date) => {
      const month = new Date(date);
      return months[month.getMonth()];
    };
  }, []);

  const CustomInput = useMemo(() => {
    return forwardRef<HTMLButtonElement, ButtonProps>(
      ({ value, onClick }, ref) => (
        <button
          className="btn datepicker_button"
          type="button"
          onClick={(val) => {
            onClick(val);
            if (!open) {
              setOpen(true);
            } else {
              datePickerRef.current.setOpen(false);
              setOpen(false);
            }
          }}
          ref={ref}
        >
          <Icon className="icon-padding" identifier="calendar-today" />
          <span>{start_date && end_date ? value : "Custom"} </span>
          <Icon className="icon-padding" identifier="chevron-down" />
        </button>
      )
    );
  }, [open, start_date, end_date]);

  const renderCustomHeader = useMemo(() => {
    return ({
      date,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => (
      <div className="header-wrapper">
        <span className="label">{`${getMonth(date)} ${new Date(
          date
        ).getFullYear()}`}</span>
        <div className="btn-wrapper">
          <button
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
          >
            <Icon identifier="chevron-left" />
          </button>

          <button
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            type="button"
          >
            <Icon identifier="chevron-right" />
          </button>
        </div>
      </div>
    );
  }, [getMonth]);

  const handleDateChange = useMemo(() => {
    return (dates) => {
      if (dates && Array.isArray(dates)) {
        onDateChange(dates);
        if (dates[1]) {
          setOpen(false);
        }
      }
    };
  }, [onDateChange]);

  const handleCalendarClose = useMemo(() => {
    return () => setOpen(false);
  }, []);

  return (
    <DatePicker
      dateFormat="MMM d, yyyy"
      ref={datePickerRef}
      renderCustomHeader={renderCustomHeader}
      selected={start_date}
      onChange={handleDateChange}
      startDate={start_date}
      endDate={end_date}
      selectsRange
      // customInput={<CustomInput />}
      onCalendarClose={handleCalendarClose}
      inline
      minDate={min_date}
      maxDate={max_date}
    />
  );
};

export default DatePickerComponent;
